import { browser } from '$app/environment';
import { get } from 'svelte/store';
import { app, setGlobalStore, setMetaStore } from './store';
import { COOKIES, COOKIES_CONSENT, COUNTRY_CODES_GDPR } from '$lib/utils/constants';
import { userDetectionService } from '$lib/utils/user-detection';
import Cookies from 'js-cookie';
import { Sentry } from '$lib/services/sentry';

/**
 * Represents a mapping of cookie(or source object) keys to corresponding property names
 * for meta store.
 * @type {{[key: string]: string}}
 */
const META_VAR_MAP = {
	'cloudfront-is-ios-viewer': 'isIosViewer',
	'cloudfront-viewer-postal-code': 'viewerPostalCode',
	'cloudfront-viewer-country-region': 'viewerCountryRegion',
	'cloudfront-is-android-viewer': 'isAndroidViewer',
	'cloudfront-viewer-country': 'viewerCountry',
	'cloudfront-is-desktop-viewer': 'isDesktopViewer',
	'cloudfront-is-mobile-viewer': 'isMobileViewer',
	'cloudfront-is-smarttv-viewer': 'isSmarttvViewer',
	'cloudfront-is-tablet-viewer': 'isTabletViewer',
	'x-aofl-app-version': 'appVersion',
	'x-aofl-in-app': 'inApp',
	'device_id': 'device_id_from_cookie',
	'acquisitionSource': 'acquisitionSource',
	'deviceId': 'deviceId',
	'environment': 'environment',
	'isAndroidViewer': 'isAndroidViewer',
	'isIosViewer': 'isIosViewer',
	'appStore': 'appStore',
};

/**
 * Derive a locale code using existing locale or navigator language + viewer country.
 * @param {string} localeCode - Existing locale code, if any.
 * @param {string} viewerCountry - Country code (e.g., 'US').
 * @returns {string} Derived locale code (e.g., 'en-US').
 */
export const getUserLocale = (localeCode, viewerCountry) => {
	if (localeCode) return localeCode;
	let language = 'en';
	const navigatorLocale =
		typeof navigator !== 'undefined'
			? (navigator.language && navigator.languages[0]) || navigator.language || ''
			: '';
	if (navigatorLocale) {
		try {
			if (typeof Intl !== 'undefined' && typeof Intl.Locale === 'function') {
				language = new Intl.Locale(navigatorLocale).language || language;
			} else {
				language = navigatorLocale.replace('_', '-').split('-')[0].toLowerCase();
			}
		} catch (error) {
			language = navigatorLocale.replace('_', '-').split('-')[0].toLowerCase() || language;
		}
	}
	return `${language}-${viewerCountry}`;
};

/**
 * Set the global loading flag in the app store (used by the loading service).
 * @param {boolean} value - Whether the global loading overlay is active.
 * @returns {void}
 */
export const setGlobalLoading = (value) => {
	app.set({ ...get(app), global: { ...get(app).global, loading: value } });
	Sentry.addBreadcrumb({
		category: 'ui-show',
		message: `Loading: ${value}`,
		level: 'info',
	});
};

/**
 * Sets the app version in the meta store.
 * @param {string} appVersion - The app version to set.
 * @returns {void}
 */

export const setAppVersion = (appVersion) => {
	setMetaStore({ appVersion });
};

/**
 * Updates the meta object in the store with the provided metaValues.
 * @param {{[key:string]: string | boolean }} metaValues - Meta values provided by external sources(cloudfront) about the user
 * @returns {Promise<void>}
 */
export const setMeta = async (metaValues) => {
	/** @type {{[key: string]: string | boolean | null }} */
	const updatedMeta = {};

	for (const key in metaValues) {
		if (META_VAR_MAP[key]) {
			updatedMeta[META_VAR_MAP[key]] = metaValues[key];
		}
	}
	// Rehydrating meta values with client side calculation (browser-based),
	// this is to allow for any missed determination for flags
	// added by a browser (or other client like ABCm App) after
	// the initial request.
	const clientSideMetaValues = await userDetectionService({
		navigator: navigator,
		urlSearchParams: new URLSearchParams(window?.location?.search),
	});

	const viewerCountryForLocale =
		updatedMeta.viewerCountry ??
		clientSideMetaValues.viewerCountry ??
		get(app).meta.viewerCountry ??
		'US';

	const derivedLocaleCode = getUserLocale(
		updatedMeta.localeCode ?? get(app).meta.localeCode,
		viewerCountryForLocale
	);

	await setMetaStore({
		...updatedMeta,
		...clientSideMetaValues,
		localeCode: derivedLocaleCode,
	});
};

/**
 * Initializes cookies and store values.
 * @returns {Promise<void>}
 */
export const initializeAnalyticsCookiesStore = async () => {
	const analyticsCookie = Cookies.get(COOKIES_CONSENT.ANALYTICS) ?? 'true';
	const targetingCookie = Cookies.get(COOKIES_CONSENT.TARGETING) ?? 'true';

	Cookies.set(COOKIES_CONSENT.ANALYTICS, analyticsCookie, COOKIES.CONFIG);
	Cookies.set(COOKIES_CONSENT.TARGETING, targetingCookie, COOKIES.CONFIG);

	await setGlobalStore({
		analyticsCookiesEnabled: analyticsCookie === 'true',
		targetingCookiesEnabled: targetingCookie === 'true',
	});
};

/**
 * Sets the consent status for analytics and targeting cookies.
 * @param {boolean} value - The consent value for analytics cookies.
 * @returns {Promise<void>}
 */
export const setCookieConsent = async (value) => {
	Cookies.set(COOKIES_CONSENT.ANALYTICS, value.toString());
	Cookies.set(COOKIES_CONSENT.TARGETING, value.toString());

	await setGlobalStore({
		analyticsCookiesEnabled: value,
		targetingCookiesEnabled: value,
	});
};

/**
 * Shared load gate for third-party marketing vendors (GTM tags, Hotjar).
 *
 * Recomputed per call rather than cached: consent can be revoked mid-session,
 * and this store is persisted, so a stored flag would outlive the state it came
 * from and reload vendors for a visitor who had already opted out.
 * @returns {boolean}
 */
export const canLoadMarketingVendors = () => {
	if (!browser) return false;

	const { global, meta } = get(app);

	return (
		!navigator.globalPrivacyControl &&
		!meta.inApp &&
		global.analyticsCookiesEnabled === true &&
		global.targetingCookiesEnabled === true
	);
};

/**
 * Sets the GDPR visibility state based on the viewer's country.
 * @param {string | boolean} viewerCountry - The country code of the viewer (e.g., 'US', 'GB').
 * @param {boolean} setCookie - Optional parameter to set a cookie and hide the GDPR banner.
 * @returns {Promise<void>}
 */
export const setGdprVisibility = async (viewerCountry, setCookie = false) => {
	if (Cookies.get(COOKIES.GDPR)) return;

	const isGdprCountry =
		typeof viewerCountry === 'boolean'
			? viewerCountry
			: COUNTRY_CODES_GDPR[viewerCountry] !== undefined;
	if (isGdprCountry) await setGlobalStore({ gdprVisible: isGdprCountry });

	if (setCookie) {
		Cookies.set(COOKIES.GDPR, 'hide', COOKIES.CONFIG);
		await setGlobalStore({ gdprVisible: false });
	}
};

export const isMobileViewer = () => {
	const isMobileViewer = String(get(app).meta.isMobileViewer).toLowerCase() === 'true';
	const isTabletViewer = String(get(app).meta.isTabletViewer).toLowerCase() === 'true';
	const isIosViewer = String(get(app).meta.isIosViewer).toLowerCase() === 'true';
	const isAndroidViewer = String(get(app).meta.isAndroidViewer).toLowerCase() === 'true';
	return isMobileViewer || isTabletViewer || isIosViewer || isAndroidViewer;
};

/**
 * Returns true when the viewer is an iOS device (iPhone/iPad), including iPadOS
 * which reports as a touch-capable Mac.
 * @param {import('./types').Meta} [meta] - Meta to evaluate; defaults to the current app store meta.
 * @returns {boolean}
 */
export const isIosViewer = (meta = get(app).meta) =>
	Boolean(meta?.isIosViewer) || Boolean(meta?.isMac && meta?.isTabletViewer);

/**
 * Returns true when the viewer is an Android device (excluding Amazon Fire devices,
 * which report as Amazon viewers and are checked separately).
 * @param {import('./types').Meta} [meta] - Meta to evaluate; defaults to the current app store meta.
 * @returns {boolean}
 */
export const isAndroidViewer = (meta = get(app).meta) =>
	Boolean(meta?.isAndroidViewer) && !meta?.isAmazonViewer;

/**
 * Gets the desktop product selected on the desktop-choice page.
 * @returns {import('$lib/utils/constants').DesktopChoice | undefined}
 */
export function getDesktopChoice() {
	return get(app).global.desktopChoice ?? undefined;
}

/**
 * @param {import('$lib/utils/constants').DesktopChoice} desktopChoice - Desktop product selected on the desktop-choice page.
 * @returns {void}
 */
export function setDesktopChoice(desktopChoice) {
	setGlobalStore({ desktopChoice });
}

/**
 * Clears the stored desktop product choice (e.g. on logout).
 * @returns {void}
 */
export function resetDesktopChoice() {
	setGlobalStore({ desktopChoice: '' });
}
